import {
  CollectionViewer,
  DataSource,
  ListRange,
} from '@angular/cdk/collections';
import { signal } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  filter,
  map,
  startWith,
  switchMap,
  tap,
} from 'rxjs';

export type DataProvider<T, TFilters> = (req: {
  range: ListRange;
  filters: TFilters;
  sortParams: { column: keyof T; order: 'asc' | 'desc' }[];
}) => Observable<{ items: T[]; total: number }>;

export class AbbTableDataSource<T, TFilters> extends DataSource<T> {
  total = signal(0);
  items = signal<T[]>([]);
  protected _filters$ = new BehaviorSubject<TFilters | undefined>(undefined);
  protected _dataProvider$ = new BehaviorSubject<
    DataProvider<T, TFilters> | undefined
  >(undefined);
  protected _paginator$ = new BehaviorSubject<MatPaginator | undefined>(
    undefined
  );
  protected _sortBy$ = new BehaviorSubject<
    { column: keyof T; order: 'asc' | 'desc' }[]
  >([]);

  setPaginator(paginator: MatPaginator) {
    this._paginator$.next(paginator);
  }
  getLastFilter() {
    return this._filters$.value;
  }
  setDataProvider(dataProvider: DataProvider<T, TFilters>) {
    this._dataProvider$.next(dataProvider);
  }
  setFilters(filters: TFilters) {
    this._filters$.next(filters);
  }
  setSortParams(sortBy: { column: keyof T; order: 'asc' | 'desc' }[]) {
    this._sortBy$.next(sortBy);
  }
  override connect(
    collectionViewer: CollectionViewer
  ): Observable<readonly T[]> {
    return combineLatest([
      this._paginator$.pipe(
        filter((v) => v !== undefined),
        switchMap((v) => v!.page),
        startWith({ pageIndex: 0, pageSize: 8 })
      ),
      this._filters$.pipe(filter((v) => v !== undefined)),
      this._dataProvider$.pipe(filter((v) => v !== undefined)),
      this._sortBy$,
    ]).pipe(
      debounceTime(100),
      switchMap(([page, filters, dataProvider, sortParams]) =>
        dataProvider!({
          range: {
            end: (page.pageIndex + 1) * page.pageSize,
            start: page.pageIndex * page.pageSize,
          },
          filters: filters as TFilters,
          sortParams,
        })
      ),
      tap((res) => {
        this.total.set(res.total);
        this.items.set(res.items);
      }),
      map((res) => res.items)
    );
  }
  override disconnect(): void {}
}
